import {Breakpoint, Dialog, SwipeableDrawer, useTheme} from '@mui/material';
import {PropsWithChildren, useCallback, useLayoutEffect} from 'react';
import {styled} from '@mui/material/styles';
import {paperClasses} from '@mui/material/Paper';
import useResizeObserver from 'use-resize-observer';
import {isNumber} from 'lodash';
import {isString} from 'lodash/fp';

import {useIsDownSm} from 'utils/hooks/useMediaQuery';
import {isIOS} from 'utils/deviceInfo';
import {shouldNotForwardProp} from 'utils/styled';

import {
  DecisionsDialogTitle,
  DecisionsDialogTitleCloseType,
} from 'modules/decisionsNext/components/DecisionsDialog/DecisionsDialogTitle';

const SSwipeableDrawer = styled(
  SwipeableDrawer,
  shouldNotForwardProp('minHeight', 'fixedSwiperHeight')
)<{minHeight?: number; fixedSwiperHeight?: boolean}>(({theme, minHeight, fixedSwiperHeight}) => ({
  zIndex: theme.zIndex.modal,
  [`.${paperClasses.root}`]: {
    overflow: 'hidden',
    maxHeight: [`calc(100vh - ${theme.spacing(6)})`, `calc(100dvh - ${theme.spacing(6)})`],
    ...(fixedSwiperHeight ? {height: `calc(100vh - ${theme.spacing(30)})`} : {}),
    ...(minHeight ? {minHeight: minHeight} : {}),
  },
}));

const SSwiperContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  overflow: 'hidden',
}));

const SDialog = styled(
  Dialog,
  shouldNotForwardProp('maxDialogWidth', 'minHeight', 'dialogHeight')
)<{
  maxDialogWidth?: number;
  minHeight?: number;
  dialogHeight?: number | string;
}>(({maxDialogWidth, minHeight, dialogHeight}) => ({
  [`.${paperClasses.root}`]: {
    ...(maxDialogWidth ? {maxWidth: maxDialogWidth} : {}),
    ...(minHeight ? {minHeight: minHeight} : {}),
    ...(dialogHeight ? {height: dialogHeight} : {}),
  },
}));

export type DecisionsDialogProps = DataTestId & {
  /**
   * { dialog: 'icon', drawer: 'text' } by default
   */
  closeType?:
    | DecisionsDialogTitleCloseType
    | {
        dialog?: DecisionsDialogTitleCloseType;
        drawer?: DecisionsDialogTitleCloseType;
      };
  open: boolean;
  title: string;
  /**
      xs: 360px

      sm: 600px (default)

      md: 900px

      lg: 1024px

      xl: 1200px
     */
  maxWidth?: Breakpoint | number | false;
  minHeight?: number;
  headless?: boolean;
  fixedSwiperHeight?: boolean;
  dialogHeight?: string | number;
  disableSwipeable?: boolean;
  onClose: () => void;
  onExited?: () => void;
  onBack?: () => void;
};

export const DecisionsDialog = ({
  dataTestId,
  children,
  open,
  onClose,
  onExited,
  maxWidth,
  dialogHeight,
  minHeight,
  title,
  headless,
  disableSwipeable,
  fixedSwiperHeight,
  closeType = {
    dialog: 'icon',
    drawer: 'text',
  },
  onBack,
}: PropsWithChildren<DecisionsDialogProps>) => {
  const isDownSm = useIsDownSm();
  const theme = useTheme();
  const {ref, height} = useResizeObserver<HTMLDivElement>();
  const isNumberMaxWidth = isNumber(maxWidth);

  useLayoutEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        const paperNode = document.querySelector<HTMLDivElement>(`.${paperClasses.root}`);

        if (paperNode) {
          paperNode.style.maxHeight = `${window.innerHeight - parseInt(theme.spacing(6))}px`;
        }
      }, 10);
    };

    if (open && isDownSm) {
      handleResize();
      window.addEventListener('focusin', handleResize);
      window.addEventListener('focusout', handleResize);
    }

    return () => {
      window.removeEventListener('focusin', handleResize);
      window.removeEventListener('focusout', handleResize);
    };
  }, [open, height, isDownSm, theme]);

  /**
   * When disableSwipeable is true, mark touch events with defaultMuiPrevented
   * so SwipeableDrawer completely ignores them (no visual tracking, no close).
   * This is the internal MUI mechanism to prevent swipe handling.
   */
  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (disableSwipeable) {
        (event.nativeEvent as any).defaultMuiPrevented = true;
      }
    },
    [disableSwipeable]
  );

  if (isDownSm) {
    return (
      <SSwipeableDrawer
        data-testid={dataTestId}
        anchor='bottom'
        variant='temporary'
        open={open}
        /**
         * This exact version of SwipeableDrawer (with enabled disableSwipeToOpen and disableDiscovery props)
         * is never going to fire onOpen, so it's safe to leave it as undefined to avoid passing unnecessary props
         */
        onOpen={() => undefined}
        onClose={onClose}
        ModalProps={{
          keepMounted: false,
          onTransitionExited: onExited,
        }}
        disableSwipeToOpen
        disableDiscovery
        disableBackdropTransition={isIOS}
        minHeight={minHeight}
        fixedSwiperHeight={fixedSwiperHeight}
      >
        {!headless && (
          <DecisionsDialogTitle
            title={title}
            titleAlign='left'
            onClose={onClose}
            onBack={onBack}
            closeType={isString(closeType) ? closeType : closeType.drawer}
          />
        )}
        <SSwiperContent ref={ref} onTouchStart={handleTouchStart}>
          {children}
        </SSwiperContent>
      </SSwipeableDrawer>
    );
  }

  return (
    <SDialog
      slotProps={{
        root: {
          'data-testid': dataTestId,
        } as any,
      }}
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited,
      }}
      fullWidth
      maxWidth={isNumberMaxWidth ? undefined : maxWidth}
      maxDialogWidth={isNumberMaxWidth ? maxWidth : undefined}
      minHeight={minHeight}
      dialogHeight={dialogHeight}
    >
      {!headless && (
        <DecisionsDialogTitle
          title={title}
          titleAlign='center'
          onBack={onBack}
          onClose={onClose}
          closeType={isString(closeType) ? closeType : closeType.dialog}
        />
      )}
      {children}
    </SDialog>
  );
};
